import React, { useState, useEffect, useMemo } from "react"
import { Button, Typography, Paper } from "@material-ui/core"
import { RailsForm } from '@support/RailsForm'
import { Form as FinalForm } from "react-final-form"
import Field from '@ui/Fields/Field'
import { format } from "date-fns"
import { fetchEquipments, fetchAreas, buildQueryWithBrackets } from "@models/admin/reports"
import styles from '@theme/styles/forms'
import withStyles from '@material-ui/core/styles/withStyles'
import LoadingComponent from '@ui/LoadingComponent'
import { getToken } from '@services/authStore'
import { useErrorHandler } from '@contexts/ErrorHandlerProvider'


const ClimateReport = ({ classes }) => {
  const [areas, setAreas] = useState([])
  const [stations, setStations] = useState([])
  const [pluviometers, setPluviometers] = useState([])
  const [organizationId, setOrganizationId] = useState('')
  const [dataLoaded, setDataLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const handleError = useErrorHandler()

  useEffect(() => {
    ( async () => {
      try {
        const areasData = await fetchAreas(4526)
        setAreas(areasData.map(({ id, name }) => ({ id, name })))
      } catch (error) {
        handleError(error)
      }
    })()
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      if (!organizationId) {
        setStations([])
        setPluviometers([])
        setDataLoaded(false)
        return
      }

      try {
        const equipments = await fetchEquipments(organizationId)
        const listStations = equipments.filter(e => e.tags?.includes("weather_station"))
        const listPluviometers = equipments.filter(e => e.tags?.includes("pluviometer"))

        setStations(listStations.map(({ id, name }) => ({ id, name })))
        setPluviometers(listPluviometers.map(({ id, name }) => ({ id, name })))
        setDataLoaded(true)
      } catch (error) {
        handleError(error)
        setDataLoaded(false)
      }
    }

    const timeoutId = setTimeout(() => {
      fetchData()
    }, 1000);

    return () => clearTimeout(timeoutId)
  }, [organizationId])

  const handleSubmit = async (data) => {
    const { start, finish, ...rest } = data;
    const formattedStart = format(new Date(start), "yyyy-MM-dd");
    const formattedFinish = format(new Date(finish), "yyyy-MM-dd");
    const params = {
      ...rest,
      start: formattedStart,
      finish: formattedFinish,
      token: getToken(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    const query = buildQueryWithBrackets(params)
    const url = `https://reports.cultivointeligente.com.br/run_report/climate_report?${query}`

    window.open(url, '_blank')
  }

  const today = useMemo(() => new Date(), [])
  const initialValues = useMemo(() => ({
    start: today,
    finish: today,
    organization_id: '',
    station_ids: [],
    pluviometer_ids: [],
  }), [today])

  return (
    <>
      {loading ? <LoadingComponent /> : (
        <RailsForm
          component={FinalForm}
          action="create"
          url={`https://reports.cultivointeligente.com.br/run_report/climate_report`}
          handleSubmit={handleSubmit}
          initialValues={initialValues}
          render={({ handleSubmit, values }) => {
            if (values.organization_id !== organizationId) {
              setOrganizationId(values.organization_id || '')
            }

            return (
              <Paper className={classes.form__wrapper} elevation={0}>
                <form className={classes.form} onSubmit={handleSubmit}>
                  <header className={classes.form__header}>
                    <Typography component="h2" variant="h5">
                      {I18n.t("activerecord.models.v4/reports.climate")}
                    </Typography>
                  </header>
                  <main className={classes.form__fields}>
                    <Field type="date" label="Data Inicial" name="start" required />
                    <Field type="date" label="Data Final" name="finish" required />
                    <Field type="text" label="Organization" name="organization_id" required />
                    <Field type="select" data={areas} label="Área" name="area_id" required />
                    {dataLoaded && (
                      <>
                        <Field type="multiSelect"
                          data={stations}
                          label="Estações Meteorológicas"
                          name="station_ids"
                          required
                        />
                        <Field
                          type="multiSelect"
                          data={pluviometers}
                          label="Pluviômetros"
                          name="pluviometer_ids"
                          required
                        />
                      </>
                    )}
                  </main>
                  <footer className={classes.form__actions}>
                    <Button
                      className={classes.form__action}
                      color="primary"
                      type="submit"
                      variant="contained"
                    >
                      Gerar Relatório
                    </Button>
                  </footer>
                </form>
              </Paper>
            )
          }}
        />
      )}
    </>
  )
}

export default withStyles(styles)(ClimateReport)
